import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import SearchRounded from "@mui/icons-material/SearchRounded";
import { getCatalogCategories } from "./services/catalogMappingService";
import { findCategoryInTree, flattenCategoryNodes } from "./utils/categoryPickerUtils";

const normalizeSearchText = (value = "") =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const truncateText = (value, maxLength = 220) => {
  const text = String(value || "").trim();
  if (!text) {
    return "—";
  }
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

const CatalogCategoryPickerDialog = ({
  open,
  currentCategoryId = null,
  currentPath = "",
  saving = false,
  onClose,
  onSelect,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [macroCategories, setMacroCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeMacroId, setActiveMacroId] = useState(null);
  const [pendingSelection, setPendingSelection] = useState(null);

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setActiveMacroId(null);
      setPendingSelection(null);
      setLoadError("");
      return undefined;
    }

    let cancelled = false;
    const loadCategories = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const payload = await getCatalogCategories();
        if (cancelled) {
          return;
        }
        const macros = payload?.macro_categorie || [];
        setMacroCategories(macros);
      } catch (error) {
        if (!cancelled) {
          setLoadError(error.message || "Impossibile caricare le categorie");
          setMacroCategories([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadCategories();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || loading || macroCategories.length === 0) {
      return;
    }
    const match = findCategoryInTree(macroCategories, currentCategoryId);
    if (!match) {
      setPendingSelection(null);
      return;
    }
    setActiveMacroId(match.macroId);
    setPendingSelection({
      categoryId: match.categoryId,
      pathLabels: match.pathLabels,
      alberoCategorie: (match.pathLabels || []).join(";"),
    });
  }, [open, loading, macroCategories, currentCategoryId]);

  const trimmedQuery = searchQuery.trim();
  const isSearching = trimmedQuery.length >= 2;

  const searchResults = useMemo(() => {
    if (!isSearching) {
      return [];
    }
    const query = normalizeSearchText(trimmedQuery);
    const results = flattenCategoryNodes(macroCategories)
      .filter((node) => {
        const pathText = (node.pathLabels || []).join(" ");
        return normalizeSearchText(node.label).includes(query)
          || normalizeSearchText(pathText).includes(query);
      })
      .map((node) => ({
        type: node.type,
        label: node.label,
        macro: node.macro,
        categoryId: node.categoryId,
        pathLabels: node.pathLabels,
        depth: node.depth,
      }));

    return results.slice(0, 32);
  }, [isSearching, macroCategories, trimmedQuery]);

  const activeMacro = useMemo(
    () => macroCategories.find((item) => item.macroId === activeMacroId) || null,
    [activeMacroId, macroCategories],
  );

  const handleSelect = (categoryId, pathLabels) => {
    setPendingSelection({
      categoryId,
      pathLabels,
      alberoCategorie: (pathLabels || []).join(";"),
    });
  };

  const handleApply = () => {
    if (!pendingSelection) {
      return;
    }
    onSelect?.(pendingSelection);
  };

  const isSelected = (categoryId) => Number(pendingSelection?.categoryId) === Number(categoryId);

  const currentCategoryLabel = currentPath
    || (currentCategoryId != null && String(currentCategoryId) !== "" ? String(currentCategoryId) : "—");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ className: "cm-dialog cm-category-picker-dialog" }}
    >
      <DialogTitle className="cm-dialog-title">
        Seleziona categoria prodotto
      </DialogTitle>
      <DialogContent className="cm-dialog-body cm-category-picker-shell">
        <section className="cm-category-picker-context">
          <div className="cm-category-picker-selection-banner">
            <div className="cm-category-picker-selection-main">
              <span className="cm-field-label">Categoria selezionata</span>
              {pendingSelection?.pathLabels?.length ? (
                <span className="cm-category-picker-selected-chip">
                  {pendingSelection.pathLabels.join(" > ")}
                </span>
              ) : (
                <p className="cm-category-picker-context-value">
                  {truncateText(currentCategoryLabel, 220)}
                </p>
              )}
            </div>
            <div className="cm-category-picker-selection-id">
              <span className="cm-field-label">ID categoria</span>
              <span className="cm-category-picker-id-value">
                {pendingSelection?.categoryId ?? currentCategoryId ?? "—"}
              </span>
            </div>
          </div>
        </section>

        <section className="cm-category-picker-toolbar">
          <TextField
            size="small"
            fullWidth
            placeholder="Cerca categoria..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded fontSize="small" />
                </InputAdornment>
              ),
            }}
            className="cm-category-picker-search"
          />
        </section>

        <section className="cm-category-picker-main">
          {loading && (
            <div className="cm-category-picker-loading">
              <CircularProgress size={28} />
            </div>
          )}

          {!loading && loadError && (
            <Typography color="error" variant="body2">
              {loadError}
            </Typography>
          )}

          {!loading && !loadError && isSearching && (
            <div className="cm-category-picker-search-panel">
              <Typography variant="body2" className="cm-category-picker-search-count">
                {searchResults.length} risultat{searchResults.length === 1 ? "o" : "i"} per &quot;{trimmedQuery}&quot;
              </Typography>
              <div className="cm-category-picker-search-grid">
                {searchResults.length === 0 ? (
                  <Typography variant="body2">Nessuna categoria trovata.</Typography>
                ) : (
                  searchResults.map((result) => (
                    <button
                      key={`${result.type}-${result.categoryId}-${result.label}`}
                      type="button"
                      className={`cm-category-picker-card${result.type === "macro" ? " is-macro" : ""}${isSelected(result.categoryId) ? " is-selected" : ""}`}
                      onClick={() => handleSelect(result.categoryId, result.pathLabels)}
                      disabled={saving}
                    >
                      {result.type === "micro" ? `${(result.pathLabels || []).join(" > ")}` : result.label}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          {!loading && !loadError && !isSearching && (
            <div className="cm-category-picker-browse">
              <div className="cm-category-picker-macros-panel">
                <Typography component="h3" className="cm-category-picker-section-title">
                  Macrocategorie
                </Typography>
                <div className="cm-category-picker-macros-list">
                  {macroCategories.map((macroItem) => (
                    <button
                      key={macroItem.macroId}
                      type="button"
                      className={`cm-category-picker-macro${activeMacroId === macroItem.macroId ? " is-active" : ""}`}
                      onClick={() => setActiveMacroId(macroItem.macroId)}
                    >
                      {macroItem.macro}
                    </button>
                  ))}
                </div>
              </div>

              <div className={`cm-category-picker-detail-panel${activeMacro ? " is-active" : ""}`}>
                <Typography component="h3" className="cm-category-picker-section-title">
                  Categorie (tutti i livelli)
                </Typography>
                {!activeMacro && (
                  <Typography variant="body2" className="cm-category-picker-empty-hint">
                    Seleziona una macrocategoria per visualizzare le sottocategorie.
                  </Typography>
                )}
                {activeMacro && (
                  <>
                    <div className="cm-category-picker-detail-head">
                      <Typography component="p" className="cm-category-picker-detail-title">
                        {activeMacro.macro}
                      </Typography>
                      <button
                        type="button"
                        className="cm-category-picker-explore-btn"
                        onClick={() => handleSelect(activeMacro.macroId, [activeMacro.macro])}
                        disabled={saving}
                      >
                        Usa macro
                      </button>
                    </div>
                    <div className="cm-category-picker-micro-grid">
                      {(activeMacro.micros || []).length === 0 ? (
                        <Typography variant="body2">Nessuna sottocategoria disponibile.</Typography>
                      ) : (
                        (activeMacro.micros || []).map((microItem) => {
                          const depth = microItem.depth || (microItem.pathLabels || []).length || 2;
                          const pathLabels = microItem.pathLabels || [activeMacro.macro, microItem.micro];
                          return (
                          <button
                            key={`${activeMacro.macroId}-${microItem.microId}`}
                            type="button"
                            className={`cm-category-picker-card cm-category-picker-card-depth-${Math.min(depth, 4)}${isSelected(microItem.microId) ? " is-selected" : ""}`}
                            onClick={() => handleSelect(microItem.microId, pathLabels)}
                            disabled={saving}
                          >
                            <span className="cm-category-picker-item-label">{microItem.micro}</span>
                            <span className="cm-category-picker-item-path">
                              {pathLabels.join(" > ")}
                            </span>
                          </button>
                          );
                        })
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </section>
      </DialogContent>
      <DialogActions className="cm-dialog-actions">
        <Button onClick={onClose} disabled={saving}>
          Annulla
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={saving || !pendingSelection}
        >
          Applica
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CatalogCategoryPickerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  productName: PropTypes.string,
  productDescription: PropTypes.string,
  currentCategoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentPath: PropTypes.string,
  saving: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};

export default CatalogCategoryPickerDialog;
